import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";

// Staff-only activity view for one organization, built on the backend audit
// log (GET admin/orgs/:id/activity). Summary tiles + a day-grouped timeline,
// failures highlighted. The audit table only records what controllers log, so
// this is a floor of activity, not a ceiling (older actions predate coverage).

const DAY_OPTIONS = [7, 30, 90];

const ACTION_LABELS = {
  SaveConnectorConfig: "Created connector config",
  UpdateConnectorConfig: "Updated connector config",
  DeleteConnectorConfig: "Deleted connector config",
  ReactivateConnectorConfig: "Reactivated connector config",
  SaveReportMetadata: "Created report",
  UpdateReportMetadata: "Updated report",
  DeleteReportMetadata: "Deleted report",
  RunReportForFile: "Ran report from file",
  RunReportForIncomingValues: "Ran report",
  ProcessPDFAndImageFileAsync: "Processed uploaded PDF",
  CreateLegalEntity: "Created legal entity",
  UpdateLegalEntity: "Updated legal entity",
  DeleteLegalEntity: "Deleted legal entity",
  ReactivateLegalEntity: "Reactivated legal entity",
  CreateBusinessUnits: "Created business unit",
  UpdateBusinessUnit: "Updated business unit",
  DeleteBusinessUnit: "Deleted business unit",
  DeleteBusinessUnits: "Deleted business units",
  ReactivateBusinessUnit: "Reactivated business unit",
  CreateSiteLocations: "Created site location",
  UpdateSiteLocation: "Updated site location",
  DeleteSiteLocation: "Deleted site location",
  ReactivateSiteLocation: "Reactivated site location",
  RegisterUser: "Added user",
  UpdateUser: "Updated user",
  DeleteUser: "Deleted user",
  DeactivateUser: "Deactivated user",
  ReactivateUser: "Reactivated user",
  AddEcoPrismAdmin: "Added EcoPrism staff",
  RemoveEcoPrismAdmin: "Removed EcoPrism staff",
  CreateParentEntity: "Organization provisioned",
  UpdateParentEntityPlan: "Plan updated",
  CleanupParentEntity: "Organization deprovisioned",
  CleanupCompanyData: "Company data wiped",
  SaveParentEntitiesPaymentMode: "Payment mode changed",
};

const FAMILIES = [
  { key: "configs", label: "Configs", test: (a) => /^(Save|Update|Delete|Reactivate)ConnectorConfig$/.test(a) },
  { key: "reports", label: "Reports", test: (a) => /^(SaveReportMetadata|UpdateReportMetadata|DeleteReportMetadata|RunReportForFile|RunReportForIncomingValues)$/.test(a) },
  { key: "uploads", label: "Uploads", test: (a) => a === "ProcessPDFAndImageFileAsync" },
  { key: "structure", label: "Org structure", test: (a) => /(LegalEntity|BusinessUnits?|SiteLocations?)$/.test(a) && !/^Check/.test(a) },
  { key: "users", label: "Users", test: (a) => /^(RegisterUser|UpdateUser|DeleteUser|DeactivateUser|ReactivateUser|AddEcoPrismAdmin|RemoveEcoPrismAdmin)$/.test(a) },
];

// Get*/List*/Check* reads are audited too and would drown the feed (every page
// load logs one). Keep them out of tiles and timeline — unless they failed.
const isRead = (a) => /^(Get|List|Check)/.test(String(a || ""));

export const labelForAction = (action) => ACTION_LABELS[action] || action || "—";

// Drop "Initiated" rows that have a matching Success/Failure for the same
// action+user within a minute — they're the same operation, logged twice.
export function collapseInitiated(rows) {
  return rows.filter((r) => {
    if (r.result !== "Initiated") return true;
    const t = Date.parse(r.auditDateTime) || 0;
    return !rows.some(
      (o) =>
        o !== r &&
        o.action === r.action &&
        o.userEmail === r.userEmail &&
        o.result !== "Initiated" &&
        Math.abs((Date.parse(o.auditDateTime) || 0) - t) <= 60000
    );
  });
}

const utcDay = (iso) => String(iso || "").slice(0, 10);
const fmtDayHeader = (day) => {
  const d = new Date(`${day}T00:00:00Z`);
  return isNaN(d) ? day : d.toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
};
const fmtTime = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "" : `${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: "UTC" })} UTC`;
};
const fmtWhen = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "—" : `${fmtDayHeader(utcDay(iso))}, ${fmtTime(iso)}`;
};

const tileStyle = {
  flex: "1 1 100px",
  minWidth: 100,
  background: "#f4f8f7",
  borderRadius: 10,
  padding: "10px 12px",
  textAlign: "center",
};

export default function OrgActivityDrawer({ org, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [days, setDays] = useState(30);
  const [failuresOnly, setFailuresOnly] = useState(false);

  const entityId = org && org.entityId;

  useEffect(() => {
    if (!entityId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_API_URL}admin/orgs/${encodeURIComponent(entityId)}/activity?days=${days}`,
          { headers: { "Content-type": "application/json", username: localStorage.getItem("userName") } }
        );
        const data = await res.json();
        if (cancelled) return;
        if (res.status === 200 && data && data.success !== false && Array.isArray(data.data)) {
          setRows(data.data);
        } else {
          setRows([]);
          setError((data && (data.error || (typeof data.data === "string" && data.data) || data.message)) || "Could not load activity.");
        }
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          setError("Could not load activity.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entityId, days]);

  // Reset view state whenever a different org is opened.
  useEffect(() => {
    setDays(30);
    setFailuresOnly(false);
    setRows([]);
    setError("");
  }, [entityId]);

  const { tiles, lastActive, groups } = useMemo(() => {
    // Last active considers everything, reads included — a read is activity.
    const last = rows.reduce((m, r) => {
      const t = Date.parse(r.auditDateTime) || 0;
      return t > m ? t : m;
    }, 0);

    const visible = collapseInitiated(rows.filter((r) => !isRead(r.action) || r.result === "Failure"));
    const counts = FAMILIES.map((f) => ({ ...f, count: visible.filter((r) => f.test(r.action)).length }));
    const failures = visible.filter((r) => r.result === "Failure").length;

    const shown = failuresOnly ? visible.filter((r) => r.result === "Failure") : visible;
    // Backend sends newest first; keep that order and bucket by UTC day.
    const byDay = [];
    for (const r of shown) {
      const day = utcDay(r.auditDateTime);
      const bucket = byDay[byDay.length - 1];
      if (bucket && bucket.day === day) bucket.items.push(r);
      else byDay.push({ day, items: [r] });
    }
    return {
      tiles: [...counts, { key: "failures", label: "Failures", count: failures }],
      lastActive: last ? new Date(last).toISOString() : null,
      groups: byDay,
    };
  }, [rows, failuresOnly]);

  return (
    <Modal show={!!org} onHide={onClose} className="custom-modal-width-config site-modal">
      <Modal.Header>
        <div className="steps__containt-hader edit-form" style={{ width: "100%" }}>
          <h3 className="title">Activity — {org && org.name}</h3>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 4 }}>
            {DAY_OPTIONS.map((d) => (
              <button
                key={d}
                type="button"
                className={`btn btn-sm ${days === d ? "btn-primary" : "btn-outline-secondary"}`}
                style={{ borderRadius: 8 }}
                onClick={() => setDays(d)}
                disabled={loading}
              >
                {d}d
              </button>
            ))}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0, fontSize: 13 }}>
            <input type="checkbox" checked={failuresOnly} onChange={(e) => setFailuresOnly(e.target.checked)} />
            Failures only
          </label>
          <span style={{ marginLeft: "auto", fontSize: 13, color: "#5b6a65" }}>
            Last active: {lastActive ? fmtWhen(lastActive) : "—"}
          </span>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {tiles.map((t) => (
            <div key={t.key} style={{ ...tileStyle, ...(t.key === "failures" && t.count > 0 ? { background: "#fdecea" } : {}) }}>
              <div style={{ fontSize: 22, fontWeight: 600, color: t.key === "failures" && t.count > 0 ? "#b02a37" : "#2f6b66" }}>{t.count}</div>
              <div style={{ fontSize: 12, color: "#5b6a65" }}>{t.label}</div>
            </div>
          ))}
        </div>

        {loading && <p style={{ color: "#5b6a65" }}>Loading activity…</p>}
        {!loading && error && <p style={{ color: "#b02a37" }}>{error}</p>}
        {!loading && !error && groups.length === 0 && (
          <p style={{ color: "#5b6a65" }}>
            No recorded activity in this window.
            <br />
            <small>Some actions predate audit coverage, so an empty feed doesn't always mean an idle organization.</small>
          </p>
        )}

        {!loading && !error && (
          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {groups.map((g) => (
              <div key={g.day} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#2f6b66", borderBottom: "1px solid #e3efed", padding: "4px 0", marginBottom: 4 }}>
                  {fmtDayHeader(g.day)}
                </div>
                {g.items.map((r, i) => {
                  const failed = r.result === "Failure";
                  return (
                    <div
                      key={`${g.day}-${i}`}
                      style={{
                        display: "flex",
                        gap: 10,
                        padding: "6px 8px",
                        borderRadius: 8,
                        alignItems: "baseline",
                        background: failed ? "#fdecea" : "transparent",
                        marginBottom: 2,
                      }}
                    >
                      <span style={{ fontSize: 12, color: "#5b6a65", whiteSpace: "nowrap" }}>{fmtTime(r.auditDateTime)}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: failed ? "#b02a37" : "#243b36", whiteSpace: "nowrap" }}>
                        {labelForAction(r.action)}
                        {failed ? " — failed" : ""}
                      </span>
                      <span style={{ fontSize: 12, color: "#5b6a65", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.userEmail}>
                        {r.userEmail}
                      </span>
                      {r.details && (
                        <span
                          style={{ fontSize: 12, color: failed ? "#b02a37" : "#77867f", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}
                          title={r.details}
                        >
                          {r.details}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="edit-form modal-buttons">
          <Button className="btn btn-cancel" onClick={onClose}>Close</Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
